import { formatDateLongEsCO } from "../../../utils/dates";
import { normalizeAdminRichText } from "../../../utils/richText";
import { EVENTO_TIPO_LINEA_LABEL } from "./eventoDetalleConstants";

export function normalizeArray(x) {
  if (!x) return [];
  if (Array.isArray(x)) return x;
  if (Array.isArray(x.results)) return x.results;
  return [];
}

export function pickTextOrHtml(x) {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x?.texto === "string") return x.texto;
  if (typeof x?.descripcion === "string") return x.descripcion;
  return "";
}

export function formatFechaAproximadaEvento(evento) {
  const raw = String(evento?.fecha_inicial ?? evento?.fecha ?? "").trim();
  if (!raw) return "—";
  if (/^\d{4}-01-01$/.test(raw)) return raw.slice(0, 4);
  return formatDateLongEsCO(raw);
}

export function formatFechaEventoResumen(evento) {
  const raw = String(evento?.fecha_inicial ?? evento?.fecha ?? "").trim();
  if (!raw) return "—";
  if (/^\d{4}-01-01$/.test(raw)) return raw.slice(0, 4);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[1]}/${match[2]}/${match[3]}`;
  return formatFechaAproximadaEvento(evento);
}

/** Texto del título sin el prefijo de fecha ("YYYY. descripción" → "descripción"). */
export function getEventoTituloSinPrefijoFecha(title) {
  const raw = String(title ?? "").trim();
  if (!raw) return "";

  const dotIndex = raw.indexOf(". ");
  if (dotIndex >= 0) {
    const afterDot = raw.slice(dotIndex + 2).trim();
    if (afterDot) return afterDot;
  }

  const dashMatch = raw.match(/^[^.]+\s[-–]\s+(.+)$/);
  if (dashMatch?.[1]?.trim()) return dashMatch[1].trim();

  return raw;
}

export function pickEventoImagenPath(timelineMeta, evento) {
  if (timelineMeta?.imagen_evento) return timelineMeta.imagen_evento;
  if (evento?.imagen_evento) return evento.imagen_evento;

  const mm = evento?.multimedia ?? evento?.media ?? {};
  const imagenes = normalizeArray(mm?.imagenes ?? mm?.fotos);
  const first = imagenes[0];
  if (!first) return "";
  if (typeof first === "string") return first;
  return (
    first.imagen ?? first.url ?? first.archivo ?? first.src ?? first.path ?? ""
  );
}

export function tipoEventoParaMasthead(evento) {
  const gen = evento?.generalidades;
  const gTipo = parseGeneralidadFieldWithSources(
    gen?.tipo_actividad,
  ).displayText;
  if (gTipo) return gTipo;
  const k = evento?.tipo;
  if (k && EVENTO_TIPO_LINEA_LABEL[k]) return EVENTO_TIPO_LINEA_LABEL[k];
  return k ? String(k) : "—";
}

export function normalizeFenomenoAsociado(row) {
  if (!row || typeof row !== "object") return row;
  const detalle =
    row.detalle && typeof row.detalle === "object" ? row.detalle : {};
  return {
    ...row,
    tipo_fenomeno_asociado: String(
      row.tipo_fenomeno_asociado ?? row.tipo_fenomeno ?? row.tipo ?? "",
    ).trim(),
    referencia_bibliografica: row.referencia_bibliografica ?? "",
    detalle,
    imagenes: normalizeArray(row.imagenes),
  };
}

export function normalizeParametroEruptivo(row) {
  if (!row || typeof row !== "object") return row;
  const detalle =
    row.detalle && typeof row.detalle === "object" ? row.detalle : {};
  return {
    ...row,
    tipo_producto_eruptivo: String(
      row.tipo_producto_eruptivo ?? row.tipo_producto ?? row.tipo ?? "",
    ).trim(),
    referencia_bibliografica: row.referencia_bibliografica ?? "",
    detalle,
    imagenes: normalizeArray(row.imagenes),
  };
}

export function parseDatosGeologicos(data) {
  const raw =
    data?.datos_geologicos ??
    data?.detalle_ampliado?.datos_geologicos ??
    data?.geologia ??
    data?.datos_geologicos_json ??
    null;
  if (!raw) return { parametros: [], fenomenos: [], referencias: [] };

  const asObj =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        })()
      : raw;

  if (!asObj || typeof asObj !== "object")
    return { parametros: [], fenomenos: [], referencias: [] };

  const parametros = normalizeArray(
    asObj.parametros_eruptivos ??
      asObj.parametrosEruptivos ??
      asObj.parametros ??
      asObj.parametro ??
      asObj.parametros_geologicos,
  ).map(normalizeParametroEruptivo);

  const fenomenos = normalizeArray(
    asObj.fenomenos_asociados ??
      asObj.fenomenosAsociados ??
      asObj.fenomenos ??
      asObj.fenomeno,
  ).map(normalizeFenomenoAsociado);

  const referencias = normalizeArray(
    asObj.referencias_especializadas ??
      asObj.referenciasEspecializadas ??
      asObj.referencias ??
      asObj.referencia ??
      asObj.bibliografia,
  );

  return { parametros, fenomenos, referencias };
}

export function splitRichTextBlocks(html) {
  const normalized = normalizeAdminRichText(String(html ?? "").trim());
  if (!normalized) return [];

  const paragraphMatches = normalized.match(/<p\b[^>]*>[\s\S]*?<\/p>/gi);
  if (paragraphMatches && paragraphMatches.length > 0) {
    return paragraphMatches.map((block) => normalizeAdminRichText(block));
  }

  const withBreaks = normalized
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>\s*<p[^>]*>/gi, "\n\n")
    .replace(/<[^>]+>/g, "")
    .trim();

  if (!withBreaks) return [];

  return withBreaks
    .split(/\n\s*\n+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => `<p>${part}</p>`);
}

function splitByCommaOutsideParens(text) {
  const parts = [];
  let current = "";
  let depth = 0;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    else if (ch === "," && depth === 0) {
      if (current.trim()) parts.push(current.trim());
      current = "";
      continue;
    }
    current += ch;
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
}

function parseSegmentWithSource(segment) {
  const trimmed = String(segment ?? "").trim();
  if (!trimmed) return null;

  let depth = 0;
  let sourceStart = -1;

  for (let i = 0; i < trimmed.length; i += 1) {
    const ch = trimmed[i];
    if (ch === "(") {
      if (depth === 0) sourceStart = i;
      depth += 1;
    } else if (ch === ")") {
      depth -= 1;
      if (depth === 0 && sourceStart >= 0) {
        const tail = trimmed.slice(i + 1).trim();
        if (!tail) {
          const value = trimmed.slice(0, sourceStart).trim();
          if (!value) return null;
          return {
            value,
            sourceEntry: trimmed,
          };
        }
      }
    }
  }

  return {
    value: trimmed,
    sourceEntry: trimmed,
  };
}

function splitByCloseParenPeriod(text) {
  const parts = [];
  let current = "";
  let depth = 0;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);

    if (depth === 0 && ch === "." && text[i + 1] === " ") {
      if (current.trim()) parts.push(current.trim());
      current = "";
      i += 1;
      continue;
    }
    current += ch;
  }

  if (current.trim()) parts.push(current.trim());
  return parts;
}

function cleanEstiloEruptivoSegment(segment) {
  return String(segment ?? "")
    .trim()
    .replace(/\s+y\s*$/i, "")
    .trim();
}

export function parseEstiloEruptivoField(raw) {
  const text = String(raw ?? "").trim();
  if (!text) {
    return { displayText: "", displayValues: [], sourceEntries: [] };
  }

  let segments = splitByCloseParenPeriod(text);
  if (segments.length <= 1) {
    segments = splitByCommaOutsideParens(text);
  }

  const displayValues = [];
  const sourceEntries = [];

  for (const segment of segments) {
    const cleaned = cleanEstiloEruptivoSegment(segment);
    const parsed = parseSegmentWithSource(cleaned);
    if (!parsed) continue;
    displayValues.push(parsed.value);
    sourceEntries.push(parsed.sourceEntry);
  }

  return {
    displayText: displayValues.join(", "),
    displayValues,
    sourceEntries,
  };
}

export function parseGeneralidadFieldWithSources(raw) {
  const text = String(raw ?? "").trim();
  if (!text) {
    return { displayText: "", displayValues: [], sourceEntries: [] };
  }

  const displayValues = [];
  const sourceEntries = [];

  for (const segment of splitByCommaOutsideParens(text)) {
    const parsed = parseSegmentWithSource(segment);
    if (!parsed) continue;
    displayValues.push(parsed.value);
    sourceEntries.push(parsed.sourceEntry);
  }

  return {
    displayText: displayValues.join(", "),
    displayValues,
    sourceEntries,
  };
}

export function expandVeiDisplayValues(displayValues) {
  const values = new Set();

  for (const text of displayValues) {
    const parts = String(text ?? "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    for (const part of parts) {
      const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
      if (rangeMatch) {
        const start = Number(rangeMatch[1]);
        const end = Number(rangeMatch[2]);
        const lo = Math.min(start, end);
        const hi = Math.max(start, end);
        for (let n = lo; n <= hi; n += 1) {
          if (n >= 0 && n <= 8) values.add(n);
        }
        continue;
      }

      const nums = part.match(/\d+/g)?.map(Number) ?? [];
      for (const n of nums) {
        if (n >= 0 && n <= 8) values.add(n);
      }
    }
  }

  return [...values].sort((a, b) => a - b);
}

export function buildGeneralidadesFuentesHtml({
  tipoActividadRaw,
  veiRaw,
  estiloEruptivoRaw,
  fuentesRaw,
}) {
  const escapeHtml = (text) =>
    String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const blocks = [];
  const tipoFuentes =
    parseGeneralidadFieldWithSources(tipoActividadRaw).sourceEntries;
  const veiFuentes = parseGeneralidadFieldWithSources(veiRaw).sourceEntries;
  const estiloFuentes =
    parseEstiloEruptivoField(estiloEruptivoRaw).sourceEntries;
  const extraFuentes = String(fuentesRaw ?? "").trim();

  if (tipoFuentes.length > 0) {
    blocks.push(
      `<p><strong>Tipo de actividad:</strong> ${escapeHtml(tipoFuentes.join(", "))}</p>`,
    );
  }

  if (veiFuentes.length > 0) {
    blocks.push(
      `<p><strong>Índice de Explosividad Volcánica (VEI):</strong> ${escapeHtml(veiFuentes.join(", "))}</p>`,
    );
  }

  if (estiloFuentes.length > 0) {
    blocks.push(
      `<p><strong>Estilo eruptivo:</strong> ${escapeHtml(estiloFuentes.join(", "))}</p>`,
    );
  }

  if (extraFuentes) {
    blocks.push(normalizeAdminRichText(extraFuentes));
  }

  return blocks.join("");
}

export function getInformacionHistorica(evento) {
  return (
    evento?.informacion_historica ??
    evento?.info_historica ??
    evento?.historico ??
    {}
  );
}

export function hasArbolFuentesContent(evento) {
  const ih = getInformacionHistorica(evento);
  const imagenArbolFuentes =
    ih?.imagen_arbol_fuentes ??
    ih?.imagenArbolFuentes ??
    ih?.imagen_arbol ??
    null;
  if (imagenArbolFuentes) return true;

  const arbolRaw =
    ih?.arbol_fuentes ?? ih?.arbol_de_fuentes ?? ih?.arbolFuentes ?? null;
  if (arbolRaw != null) {
    if (typeof arbolRaw === "object") {
      if (Array.isArray(arbolRaw)) return arbolRaw.length > 0;
      return Object.keys(arbolRaw).length > 0;
    }
    if (String(arbolRaw).trim()) return true;
  }
  if (pickTextOrHtml(arbolRaw)) return true;
  if (pickTextOrHtml(ih?.generalidades)) return true;
  return false;
}

export function hasGeneralidadesContent(evento) {
  const gen =
    evento?.generalidades && typeof evento.generalidades === "object"
      ? evento.generalidades
      : {};

  const fields = [
    gen?.hora,
    gen?.tipo_actividad,
    gen?.municipios_afectados,
    gen?.indice_vei ?? gen?.vei_valor ?? gen?.vei,
    gen?.estilo_eruptivo,
    gen?.explicacion_productos,
    gen?.resumen ?? evento?.resumen,
    gen?.fuentes,
    gen?.productos_eruptivos_texto,
  ];
  if (fields.some((v) => String(v ?? "").trim())) return true;

  const productos = normalizeArray(
    gen?.productos_eruptivos ?? gen?.productosEruptivos ?? gen?.productos,
  );
  return productos.length > 0;
}

export function hasEventoNavChildContent(evento, parentKey, childKey) {
  if (!evento) return false;

  if (parentKey === "informacion_historica") {
    const ih = getInformacionHistorica(evento);
    if (childKey === "documentos") {
      return (
        normalizeArray(
          ih?.documentos_relacionados ??
            ih?.documentosRelacionados ??
            ih?.documentos,
        ).length > 0
      );
    }
    if (childKey === "arbol_fuentes") return hasArbolFuentesContent(evento);
    if (childKey === "efectos") return normalizeArray(ih?.efectos).length > 0;
    return false;
  }

  if (parentKey === "datos_geologicos") {
    const { parametros, fenomenos, referencias } = parseDatosGeologicos(evento);
    if (childKey === "parametros") return parametros.length > 0;
    if (childKey === "fenomenos") return fenomenos.length > 0;
    if (childKey === "referencias") return referencias.length > 0;
    return false;
  }

  if (parentKey === "multimedia") {
    const mm = evento?.multimedia ?? evento?.media ?? {};
    if (childKey === "imagenes") {
      return normalizeArray(mm?.imagenes ?? mm?.fotos).length > 0;
    }
    if (childKey === "videos") return normalizeArray(mm?.videos).length > 0;
    if (childKey === "infografias") {
      return normalizeArray(mm?.infografias ?? mm?.infografia).length > 0;
    }
    if (childKey === "mapas") {
      return normalizeArray(mm?.mapas ?? mm?.mapa).length > 0;
    }
    return false;
  }

  return false;
}

/** Secciones del menú superior con al menos un hijo (o contenido) disponible. */
export function getEventoNavSectionsWithContent(evento, allSections) {
  const sections = Array.isArray(allSections) ? allSections : [];
  if (!evento) return [];

  return sections
    .map((section) => {
      if (!Array.isArray(section.children) || section.children.length === 0) {
        if (section.key === "generalidades") {
          return hasGeneralidadesContent(evento) ? section : null;
        }
        return section;
      }

      const children = section.children.filter((child) =>
        hasEventoNavChildContent(evento, section.key, child.key),
      );
      if (children.length === 0) return null;
      return { ...section, children };
    })
    .filter(Boolean);
}

export function defaultSubForEventoSection(sectionKey) {
  if (sectionKey === "informacion_historica") return "documentos";
  if (sectionKey === "datos_geologicos") return "parametros";
  if (sectionKey === "multimedia") return "imagenes";
  return null;
}
